import type { ScreenRect } from "@msb/reader-bridge";
import { useEffect, useRef, useState } from "preact/hooks";
import { HIGHLIGHT_COLORS, MAX_NOTE, type Annotation, type HighlightColor } from "../annotations.ts";
import { defineMessages, useT } from "../i18n/index.ts";
import { Icon } from "../icons/Icon.tsx";

interface Props {
  annotation: Annotation;
  /** Place du passage à l'écran : la carte s'ouvre à côté (feuille du bas sur petit écran). */
  rect?: ScreenRect;
  sheet: boolean;
  onColor(color: HighlightColor): void;
  onNote(note: string): void;
  /** Explique le passage ; la note saisie est gardée. Absent quand rien ne peut répondre. */
  onExplain?: () => void;
  onDelete(): void;
  onClose(): void;
}

const messages = defineMessages(
  {
    annotation: "Annotation",
    colors: "Couleur du surlignage",
    green: "Surlignage vert",
    blue: "Surlignage bleu",
    pink: "Surlignage rose",
    explain: "Explique-moi",
    note: "Note personnelle",
    placeholder: "Écrivez votre note…",
    delete: "Supprimer",
    save: "Enregistrer",
  },
  {
    annotation: "Annotation",
    colors: "Highlight colour",
    green: "Green highlight",
    blue: "Blue highlight",
    pink: "Pink highlight",
    explain: "Explain this",
    note: "Personal note",
    placeholder: "Write your note…",
    delete: "Delete",
    save: "Save",
  },
);

const WIDTH = 320;

/** Le surlignage d'un passage : sa couleur, la note personnelle, et sa suppression. */
export function AnnotationCard({ annotation, rect, sheet, onColor, onNote, onExplain, onDelete, onClose }: Props) {
  const t = useT(messages);
  const [note, setNote] = useState(annotation.note ?? "");
  const field = useRef<HTMLTextAreaElement>(null);
  // Le clic qui a ouvert la carte (un tap sur le surlignage) se termine sur le voile qu'on vient de poser : ignoré.
  const openedAt = useRef(performance.now());
  useEffect(() => field.current?.focus(), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && save();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const style =
    !sheet && rect
      ? {
          left: `${Math.min(Math.max(8, rect.left + rect.width / 2 - WIDTH / 2), Math.max(8, window.innerWidth - WIDTH - 8))}px`,
          top: `${Math.min(rect.top + rect.height + 10, Math.max(8, window.innerHeight - 300))}px`,
        }
      : sheet
        ? undefined
        : { left: "50%", top: "28%", transform: "translateX(-50%)" };
  const save = () => {
    onNote(note);
    onClose();
  };
  const quote = annotation.locator.text?.highlight ?? "";
  return (
    <div class={sheet ? "msb-annotation-layer msb-annotation-layer--sheet" : "msb-annotation-layer"}>
      <div class="msb-annotation-scrim" onClick={() => performance.now() - openedAt.current > 450 && save()} />
      <div class="msb-annotation-card" role="dialog" aria-label={t("annotation")} style={style}>
        <blockquote class="msb-annotation-card__quote">{quote.length > 160 ? `${quote.slice(0, 160)}…` : quote}</blockquote>
        <div class="msb-annotation-card__colors" role="radiogroup" aria-label={t("colors")}>
          {HIGHLIGHT_COLORS.map((color) => (
            <button
              key={color.id}
              type="button"
              role="radio"
              aria-checked={annotation.color === color.id}
              class="msb-swatch"
              style={{ background: color.tint }}
              aria-label={t(color.id)}
              onClick={() => onColor(color.id)}
            />
          ))}
          {onExplain && (
            <button
              type="button"
              class="msb-annotation-card__explain"
              aria-label={t("explain")}
              title={t("explain")}
              onClick={() => {
                onNote(note);
                onExplain();
              }}
            >
              <Icon name="help" size={28} />
            </button>
          )}
        </div>
        <label class="msb-annotation-card__note">
          {t("note")}
          <textarea
            ref={field}
            rows={4}
            maxLength={MAX_NOTE}
            value={note}
            placeholder={t("placeholder")}
            onInput={(event) => setNote(event.currentTarget.value)}
          />
        </label>
        <div class="msb-annotation-card__actions">
          <button type="button" class="msb-pill" onClick={onDelete}>
            <Icon name="delete" size={20} />
            <span class="msb-pill__label">{t("delete")}</span>
          </button>
          <button type="button" class="msb-pill msb-pill--primary" onClick={save}>
            <span class="msb-pill__label">{t("save")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
